import { useState } from "react";
import { emailSubscriptionsService } from "@/lib/supabase";
import { useToast } from "./use-toast";

export type SubscriptionState = "idle" | "submitting" | "success" | "error";

interface UseEmailSubscriptionReturn {
  email: string;
  name: string;
  state: SubscriptionState;
  errorMessage: string | null;
  setEmail: (email: string) => void;
  setName: (name: string) => void;
  subscribe: () => Promise<boolean>;
  reset: () => void;
}

export const useEmailSubscription = (): UseEmailSubscriptionReturn => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState<SubscriptionState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();

  const reset = () => {
    setEmail("");
    setName("");
    setState("idle");
    setErrorMessage(null);
  };

  const subscribe = async (): Promise<boolean> => {
    if (!email) {
      setErrorMessage("Vui lòng nhập địa chỉ email của bạn");
      setState("error");
      return false;
    }

    try {
      setState("submitting");
      setErrorMessage(null);

      // Thử kết nối Supabase, nếu không được thì hiển thị thông báo
      const { success, error } = await emailSubscriptionsService.subscribe(
        email,
        name
      );

      if (success) {
        setState("success");
        toast({
          title: "Đăng ký thành công!",
          description:
            "Bạn đã được thêm vào danh sách nhận bản tin. Chào mừng bạn đến với cộng đồng GlowHer!",
          duration: 5000,
        });

        // Reset form sau 3 giây
        setTimeout(() => {
          reset();
        }, 3000);

        return true;
      } else {
        setState("error");
        setErrorMessage(
          error || "Có lỗi xảy ra khi đăng ký. Vui lòng thử lại sau."
        );
        toast({
          title: "Đăng ký không thành công",
          description:
            error || "Có lỗi xảy ra khi đăng ký. Vui lòng thử lại sau.",
          variant: "destructive",
          duration: 5000,
        });
        return false;
      }
    } catch (err) {
      console.error("Lỗi không mong muốn khi đăng ký email:", err);
      setState("error");
      setErrorMessage("Có lỗi xảy ra khi đăng ký. Vui lòng thử lại sau.");
      toast({
        title: "Đăng ký không thành công",
        description: "Có lỗi xảy ra khi đăng ký. Vui lòng thử lại sau.",
        variant: "destructive",
        duration: 5000,
      });
      return false;
    }
  };

  return {
    email,
    name,
    state,
    errorMessage,
    setEmail,
    setName,
    subscribe,
    reset,
  };
};
